import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  ClinicalSubmission,
  ClinicalSubmissionDetail,
  DocumentExtractionResponse,
  DocumentNormalizationSummary,
  SubmissionDocument,
} from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { submissionStatusLabel, submissionStatusVariant } from '../../lib/submissionWorkflowLabels.js';

function ExtractionCell({
  norm,
  extraction,
  onStart,
  starting,
}: {
  norm: DocumentNormalizationSummary | undefined;
  extraction: DocumentExtractionResponse | undefined;
  onStart: () => void;
  starting: boolean;
}) {
  if (!norm || norm.extractionReadiness !== 'READY_FOR_EXTRACTION' || norm.templateUnknown) {
    return <span className="text-muted text-small">—</span>;
  }

  const label = extraction?.run?.status === 'SUCCEEDED' ? 'Draft ready' : null;
  if (!label) {
    return (
      <button type="button" className="btn btn-secondary btn-sm" disabled={starting} onClick={onStart}>
        {starting ? 'Starting…' : 'Start Draft Extraction'}
      </button>
    );
  }

  return (
    <div>
      <StatusBadge status={label} variant="default" />
    </div>
  );
}

function documentStatusLabel(status: SubmissionDocument['status']): string {
  switch (status) {
    case 'READY':
      return 'Ready';
    case 'FAILED':
      return 'Processing failed';
    default:
      return 'Processing document…';
  }
}

function NormalizationDetails({ norm }: { norm: DocumentNormalizationSummary | undefined }) {
  if (!norm || norm.status === 'PENDING' || norm.status === 'PROCESSING') return null;
  if (norm.status === 'FAILED') return <p className="text-muted text-small">Processing incomplete</p>;
  return null;
}

export function ClinicalSheetsPage() {
  const navigate = useNavigate();
  const [workflowSubmissions, setWorkflowSubmissions] = useState<ClinicalSubmissionDetail[]>([]);
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [submissions, setSubmissions] = useState<ClinicalSubmission[]>([]);
  const [extractionByDocumentId, setExtractionByDocumentId] = useState<Record<string, DocumentExtractionResponse>>({});
  const [startingExtractionId, setStartingExtractionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadInFlightRef = useRef<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    return Promise.all([
      api.listWorkflowSubmissions().then((res) => setWorkflowSubmissions(res.data as ClinicalSubmissionDetail[])),
      api.listClinicalSubmissions({ page: 1, pageSize: 50 }).then(async (res) => {
        setSubmissions(res.data);
        const readyDocs = res.data.flatMap((sub) =>
          (sub.documents ?? []).filter(
            (d) => d.status === 'READY' && sub.normalizationByDocumentId?.[d.id]?.extractionReadiness === 'READY_FOR_EXTRACTION',
          ),
        );
        const entries = await Promise.all(
          readyDocs.map(async (d) => [d.id, await api.getDocumentExtraction(d.id).catch(() => undefined)] as const),
        );
        setExtractionByDocumentId(Object.fromEntries(entries.filter(([, v]) => v) as Array<[string, DocumentExtractionResponse]>));
      }),
    ]).catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load clinical sheets'));
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    if (selectedFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [selectedFile]);

  const handleCreateDemo = async () => {
    setCreatingDemo(true);
    setUploadError(null);
    try {
      const res = await api.createDemoWorkflowSubmission();
      await load();
      navigate(`/student/sheets/${res.submission.id}/review`);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Could not create demo sheet');
    } finally {
      setCreatingDemo(false);
    }
  };

  const handleStartExtraction = async (documentId: string) => {
    setStartingExtractionId(documentId);
    try {
      await api.queueDocumentExtraction(documentId);
      await load();
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Could not start draft extraction');
    } finally {
      setStartingExtractionId(null);
    }
  };

  const handleUploadSelected = async () => {
    if (!selectedFile || uploading) return;

    const idempotencyKey =
      uploadInFlightRef.current ??
      (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${selectedFile.name}-${selectedFile.size}`);
    uploadInFlightRef.current = idempotencyKey;

    setUploading(true);
    setUploadError(null);
    setDuplicateWarning(null);
    try {
      const result = await api.uploadClinicalDocument(selectedFile, 'CLINICAL_CASE_LOG', idempotencyKey);
      if (result.exactDuplicate) {
        setDuplicateWarning(
          'This file matches a sheet you uploaded before. We saved this submission anyway.',
        );
      }
      const reviewPath = result.reviewPath ?? `/student/sheets/${result.submission.id}/review`;
      navigate(reviewPath);
    } catch (err) {
      uploadInFlightRef.current = null;
      setUploadError(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      setSelectedFile(null);
    }
  };

  const handleRetry = async (documentId: string) => {
    try {
      await api.retryDocumentProcessing(documentId);
      await load();
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Retry failed');
    }
  };

  const handleViewOriginal = (documentId: string) => {
    window.open(api.documentContentUrl(documentId), '_blank', 'noopener,noreferrer');
  };

  const hasWorkflow = workflowSubmissions.length > 0;

  return (
    <div className="page">
      <PageHeader
        title="Clinical Sheets"
        subtitle="Upload your clinical case log, review each row, and submit for faculty approval"
      />

      <div className="card upload-card upload-card--primary">
        <h2 className="card-title">Upload Clinical Sheet</h2>
        {!hasWorkflow && (
          <p className="text-muted upload-guidance">
            Upload your first clinical sheet to start tracking your cases.
          </p>
        )}
        <p className="text-muted">
          Take a photo or choose a JPEG, PNG, or PDF (max 25 MB). Your original file is stored privately for review.
        </p>
        <div className="upload-actions">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,application/pdf,image/*"
            capture="environment"
            className="file-input"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              setSelectedFile(file);
              uploadInFlightRef.current = null;
            }}
          />
          {selectedFile && (
            <p className="text-small text-muted">
              Selected: {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
            </p>
          )}
          {previewUrl && (
            <img src={previewUrl} alt="Selected clinical sheet preview" className="upload-preview-thumb" />
          )}
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selectedFile || uploading}
            onClick={() => void handleUploadSelected()}
          >
            {uploading ? 'Uploading…' : 'Upload Clinical Sheet'}
          </button>
        </div>
        {uploadError && <ErrorBanner message={uploadError} />}
        {duplicateWarning && <div className="banner banner-warning">{duplicateWarning}</div>}
        {import.meta.env.DEV && (
          <button
            type="button"
            className="btn btn-secondary btn-sm upload-demo-btn"
            disabled={creatingDemo || uploading}
            onClick={() => void handleCreateDemo()}
          >
            {creatingDemo ? 'Creating…' : 'Create Demo Clinical Sheet (development)'}
          </button>
        )}
      </div>

      <div className="card">
        <h2 className="card-title">My Submissions</h2>
        {loading ? (
          <LoadingState message="Loading clinical sheets…" />
        ) : workflowSubmissions.length === 0 ? (
          <p className="text-muted">No submissions yet. Upload a clinical sheet to begin.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Updated</th>
                  <th>Status</th>
                  <th>Rows</th>
                  <th>Needs Review</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {workflowSubmissions.map((submission) => (
                  <tr key={submission.id}>
                    <td>{new Date(submission.updatedAt).toLocaleString()}</td>
                    <td>
                      <StatusBadge
                        status={submissionStatusLabel(submission.status)}
                        variant={submissionStatusVariant(submission.status)}
                      />
                    </td>
                    <td>{submission.rows.length}</td>
                    <td>{submission.reviewNeededCount}</td>
                    <td>
                      <Link to={`/student/sheets/${submission.id}/review`} className="btn btn-primary btn-sm">
                        {submission.status === 'APPROVED' ? 'View' : 'Review'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && <ErrorBanner message={error} onRetry={() => load()} />}

      {submissions.some((s) => (s.documents?.length ?? 0) > 0) && (
        <div className="card">
          <h2 className="card-title">Uploaded Files</h2>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Uploaded</th>
                  <th>Filename</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.flatMap((sub) =>
                  (sub.documents ?? []).map((doc) => {
                    const norm = sub.normalizationByDocumentId?.[doc.id];
                    return (
                      <tr key={doc.id}>
                        <td>{new Date(doc.createdAt).toLocaleString()}</td>
                        <td>{doc.originalFilename ?? 'Clinical sheet'}</td>
                        <td>
                          <StatusBadge status={documentStatusLabel(doc.status)} />
                          <NormalizationDetails norm={norm} />
                        </td>
                        <td className="actions-cell">
                          {doc.status === 'READY' && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleViewOriginal(doc.id)}
                            >
                              View Original
                            </button>
                          )}
                          {doc.status === 'FAILED' && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => void handleRetry(doc.id)}
                            >
                              Retry Processing
                            </button>
                          )}
                          <ExtractionCell
                            norm={norm}
                            extraction={extractionByDocumentId[doc.id]}
                            starting={startingExtractionId === doc.id}
                            onStart={() => void handleStartExtraction(doc.id)}
                          />
                        </td>
                      </tr>
                    );
                  }),
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
